// Types domaine
import type { Subscription } from "@/types/subscription";
import type { MembershipType, ClientStatus } from "@/types/types";

// (optionnel) Helpers de normalisation
const normalizeStatus = (s?: string | null): ClientStatus => {
  switch ((s ?? "").toLowerCase()) {
    case "active":   return "Active";
    case "inactive": return "Inactive";
    case "expired":  return "Expired";
    case "blocked":  return "Blocked";
    case "paused":   return "Paused";
    default:         return "Inactive";
  }
};

const normalizeMembershipType = (t?: string | null): MembershipType => {
  const v = (t ?? "").toLowerCase();
  switch (v) {
    case "standard":   return "Standard";
    case "premium":    return "Premium";

    default:           return (t ?? "Standard") as MembershipType;
  }
};

type DbSubscriptionRow = {
  break_date_end: string | null;
  break_date_start: string | null;
  created_at: string | null;
  end_date: string;
  gym_id: string | null;
  id: string;
  is_manually_activated: boolean | null;
  is_paused: boolean | null;
  jokers_remaining: number | null;
  jokers_total: number | null;
  payment_frequency: string | null;
  payment_method: string | null;
  price: number; // déjà number côté DB
  sessions_remaining: number | null;
  sessions_total: number | null;
  start_date: string;
  status: string;
  type: string;
  updated_at: string | null;
  user_id: string;
  /**
   * Référence vers `subscription_types`. Indispensable pour savoir quels
   * plannings (`subscription_type_calendars`) l'abonnement ouvre : sans elle,
   * tout abonnement passe pour universel et le multi-planning est invisible
   * côté interface.
   */
  subscription_type_id?: string | null;
};

// ---- Mapper principal ----
export const mapDbSubscriptionToDomain = (row: DbSubscriptionRow): Subscription => {
  return {
    id: row.id,
    user_id: row.user_id,
    type: normalizeMembershipType(row.type),
    start_date: row.start_date,
    end_date: row.end_date,
    price: typeof row.price === "number" ? row.price : Number(row.price ?? 0),

    status: normalizeStatus(row.status),

    sessions_total: row.sessions_total ?? 0,
    sessions_remaining: row.sessions_remaining ?? 0,
    jokers_total: row.jokers_total ?? 0,
    jokers_remaining: row.jokers_remaining ?? 0,

    payment_frequency: (row.payment_frequency ??
      "one-time") as Subscription["payment_frequency"],

    payment_method: (row.payment_method ??
      "cash") as Subscription["payment_method"],

    is_manually_activated: row.is_manually_activated ?? false,
    is_paused: row.is_paused ?? false,

    break_date_start: row.break_date_start,
    break_date_end: row.break_date_end,

    created_at: row.created_at ?? "",
    updated_at: row.updated_at ?? "",

    // Conservé tel quel : c'est la clé du rattachement aux plannings.
    subscription_type_id: row.subscription_type_id ?? undefined,
  };
};

// ---- Mapper pour liste ----
export const mapDbSubscriptionsToDomain = (rows: DbSubscriptionRow[] = []): Subscription[] =>
  rows.map(mapDbSubscriptionToDomain);
